import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useAuthStore } from "@/store/auth";
import { useTenantStore } from "@/store/tenant";
import type { CameraStatus, StatusFeedEvent } from "@/types";

/** Resolve the WebSocket URL for /ws/status from the configured API base.
 *
 * S-SEC-3 — no token is placed in the URL. The browser sends the httpOnly
 * access_token cookie on the WS handshake (same-origin / credentialed),
 * and the backend reads it.
 *
 * A3 — when a super-admin has scoped themselves to a tenant, the effective
 * tenant id is appended as ``?tenant_id=`` on the handshake, mirroring the
 * REST interceptor in api/client.ts. Without it the backend resolves the
 * JWT home tenant and the live feed stays pinned to the wrong tenant after a
 * switch. ``tenantId`` is null for non-super-admins (and for a super-admin in
 * Platform View), in which case no param is appended. */
function buildWsUrl(tenantId: string | null): string {
  const wsBase =
    (import.meta.env.VITE_WS_BASE_URL as string | undefined) ?? "";
  let url: URL;
  if (wsBase) {
    url = new URL(`${wsBase.replace(/\/$/, "")}/ws/status`);
  } else {
    // Derive from the API base URL.
    const apiBase =
      (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
      window.location.origin;
    url = new URL(apiBase);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.pathname = "/ws/status";
  }
  if (tenantId) {
    url.searchParams.set("tenant_id", tenantId);
  }
  return url.toString();
}


// ---------------------------------------------------------------------------
// SHARED FEED STORE (P2)
//
// ``useStatusFeed`` is called from TWO places — Layout.tsx (for cache
// invalidation, return value discarded) and CameraGridPage.tsx (for the live dot
// map). Before this, each call opened its OWN WebSocket and kept its OWN
// ``statuses`` state, so: two sockets per page, and the grid's dot map depended
// entirely on the grid's own socket surviving every remount and reconnect. A
// video wall left up all day must not depend on that.
//
// The socket and the status map now live in module scope, ref-counted across
// subscribers. The hook's public shape is unchanged, so no call site moves.
// ---------------------------------------------------------------------------
interface FeedState {
  connected: boolean;
  statuses: Record<string, CameraStatus>;
  lastEvent: StatusFeedEvent | null;
}

let sharedState: FeedState = { connected: false, statuses: {}, lastEvent: null };
const listeners = new Set<() => void>();
let refCount = 0;
let sharedWs: WebSocket | null = null;
let sharedRetry = 0;
let sharedClosed = false;
let sharedKey = "";

function emit() {
  for (const l of listeners) l();
}

function setFeedState(patch: Partial<FeedState>) {
  sharedState = { ...sharedState, ...patch };
  emit();
}

/** Exposed for tests: reset module state between cases. */
export function __resetStatusFeedForTests() {
  sharedState = { connected: false, statuses: {}, lastEvent: null };
  listeners.clear();
  refCount = 0;
  sharedWs = null;
  sharedRetry = 0;
  sharedClosed = false;
  sharedKey = "";
}

/** Exposed for tests: read the shared snapshot the hook would expose. */
export function __getStatusFeedSnapshot(): FeedState {
  return sharedState;
}

/** Exposed for tests: feed a raw envelope through the same path the socket uses. */
export function __handleStatusFeedMessage(
  data: string,
  onCameraEvent?: (cameraId: string) => void,
) {
  let payload: StatusFeedEvent;
  try {
    payload = JSON.parse(data);
  } catch {
    return;
  }
  setFeedState({ lastEvent: payload });
  if (payload.event === "status_change" && payload.camera_id && payload.status) {
    if (sharedState.statuses[payload.camera_id] !== payload.status) {
      setFeedState({
        statuses: {
          ...sharedState.statuses,
          [payload.camera_id]: payload.status,
        },
      });
    }
    onCameraEvent?.(payload.camera_id);
  }
}

/**
 * useStatusFeed subscribes to /ws/status and:
 *   1. exposes a per-camera live status map for direct binding in lists
 *   2. invalidates the React Query camera caches on each event so cached
 *      camera detail responses refresh without a hard refetch loop.
 *
 * The WS connection auto-reconnects with exponential back-off (capped at 30s)
 * and tears down on unmount.
 */
export function useStatusFeed() {
  // S-SEC-3 — gate on the logged-in user (cookie session) rather than a
  // JS-held token. The WS handshake carries the httpOnly cookie.
  const user = useAuthStore((s) => s.user);
  // A3 — follow the super-admin tenant switch (see buildWsUrl).
  const isSuperAdmin = useTenantStore((s) => s.isSuperAdmin);
  const currentTenantId = useTenantStore((s) => s.currentTenantId);
  const effectiveTenantId = isSuperAdmin ? currentTenantId : null;
  const needsTenantSelection = isSuperAdmin && !currentTenantId;
  const qc = useQueryClient();

  // Subscribe to the SHARED store. Every call site sees the same socket and the
  // same statuses map, so the grid's dot no longer depends on its own socket.
  const [snapshot, setSnapshot] = useState<FeedState>(sharedState);
  useEffect(() => {
    const listener = () => setSnapshot(sharedState);
    listeners.add(listener);
    listener();
    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    if (needsTenantSelection) return;

    // Re-key the socket when the effective tenant changes so a switch closes the
    // old socket and reopens scoped to the new tenant.
    const key = `${effectiveTenantId ?? ""}`;
    if (sharedWs && sharedKey !== key) {
      sharedClosed = true;
      sharedWs.close();
      sharedWs = null;
    }
    sharedKey = key;
    refCount += 1;

    const connect = () => {
      sharedClosed = false;
      const ws = new WebSocket(buildWsUrl(effectiveTenantId));
      sharedWs = ws;

      ws.onopen = () => {
        sharedRetry = 0;
        setFeedState({ connected: true });
      };

      ws.onmessage = (msg) => {
        __handleStatusFeedMessage(String(msg.data), (cameraId) => {
          void qc.invalidateQueries({
            queryKey: ["cameras", "detail", cameraId],
          });
        });
        const ev = sharedState.lastEvent;
        if (ev?.event === "new_alert" || ev?.event === "alert_updated") {
          void qc.invalidateQueries({ queryKey: ["alerts"] });
          void qc.invalidateQueries({ queryKey: ["dashboard"] });
        }
      };

      ws.onclose = () => {
        setFeedState({ connected: false });
        if (sharedClosed) return;
        const delay = Math.min(30_000, 1_000 * 2 ** sharedRetry);
        sharedRetry += 1;
        setTimeout(() => {
          if (!sharedClosed && refCount > 0) connect();
        }, delay);
      };

      ws.onerror = () => {
        /* browser fires onclose next */
      };
    };

    if (!sharedWs) connect();

    return () => {
      refCount = Math.max(0, refCount - 1);
      // Only tear the socket down when the LAST subscriber unmounts. Previously
      // each consumer closed its own socket on unmount, so navigating away from
      // the grid killed that page's feed entirely.
      if (refCount === 0) {
        sharedClosed = true;
        sharedWs?.close();
        sharedWs = null;
      }
    };
  }, [user, qc, effectiveTenantId, needsTenantSelection]);

  return {
    connected: snapshot.connected,
    statuses: snapshot.statuses,
    lastEvent: snapshot.lastEvent,
  };
}
